import React from 'react';
import ReactRouterPropTypes from 'react-router-prop-types';
import PropTypes from 'prop-types';
import DataManager from '@americanexpress/data-manager';
import SharedScript, { Adobe } from '@americanexpress/shared-scripts';
import { AmexDDLRegister, getAmexDataManagerConfig } from '@americanexpress/data-manager-utils';
import { DDLRegister } from '@americanexpress/react-ddl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { useSelector } from 'react-redux';

/**
 * AnalyticsWrapper is a wrapper component that includes all the necessary analytics scripts
 * and registers the page view event.
 * It also includes the ExtendReactlytics component which is necessary for the analytics to work.
 * The identifier prop is used to identify the analytics event.
 * The shouldDisablePageView prop is used to disable the page view event.
 * If shouldDisablePageView is true, the page view event will not be sent to analytics.
 * */

const AnalyticsWrapper = ({
  route,
  identifier,
  children,
  locale,
}) => {
  const loadedHolocrons = useSelector((state) => state.getIn(['holocron', 'loaded']));
  const globalHeaderData = useSelector((state) => state.getIn(['modules', 'axp-global-header']));
  const isGlobalHeaderLoaded = loadedHolocrons && loadedHolocrons.has('axp-global-header');
  const isLoggedInCardMember = isGlobalHeaderLoaded && globalHeaderData.get('authenticated');

  const obj = global.BROWSER && route.shouldDisablePageView ? {
    initialDDL: {
      historyLimit: 700,
      ddl: {
        diff: ['events'],
        version: '1.7.0',
        charset: 'UTF-8',
        user: {},
        events: [],
        components: {},
        campaigns: {},
        timestamp: new Date().toISOString(),
        view: {
          country: locale?.split('-')[1],
          language: locale?.split('-')[0],
          locale,
          location: window.location.href,
          timestamp: new Date().toISOString(),
          origin: window.location.hostname,
          title: route.pageTitle,
          viewId: window.location.pathname,
        },
      },
    },
  } : {};
  const { onTrackCall, ddlProviderProps, reactlyticsProps } = getAmexDataManagerConfig({
    ...obj,
  });
  return (
    <DataManager
      onTrackCall={onTrackCall}
      ddlProviderProps={ddlProviderProps}
      reactlyticsProps={reactlyticsProps}
    >
      {route.shouldDisablePageView ? (
        <AmexDDLRegister disableRegisterList={{ view: true }} />
      ) : (
        <AmexDDLRegister />
      )}
      <DDLRegister name="user.authenticated" value={isLoggedInCardMember} />
      <SharedScript name="one-stream-data-handler" windowCheck="oneStreamData" version="^0.1.2" />
      <SharedScript name="one-identity-session" version="^1.0.0" windowCheck="AmexSession" />
      <Adobe version="1.2.0" />
      {/* Only necessary in a root module, otherwise this is handled by oneAmexWrapper */}
      <ExtendReactlytics analytics={{ event: { identifier } }}>{children}</ExtendReactlytics>
    </DataManager>
  );
};

AnalyticsWrapper.propTypes = {
  children: PropTypes.node,
  identifier: PropTypes.string,
  locale: PropTypes.string,
  route: ReactRouterPropTypes.route,
};

AnalyticsWrapper.defaultProps = {
  children: null,
  identifier: 'axp-shop-root',
  locale: 'en-US',
  route: {},
};

export default AnalyticsWrapper;
